import moment from 'moment';

/**
 * ТЗ 0080 — выгрузка коротких ссылок в CSV. Чистая функция: подписи и
 * переводы приходят снаружи (`t` страницы), поэтому здесь нет ни i18next,
 * ни DOM — всё проверяется юнит-тестом.
 *
 * Формат тот же, что у выгрузки продавцов (`Esign/analyticsModel`): UTF-8 с
 * BOM (иначе Excel читает кириллицу и польские буквы как ANSI), разделитель
 * `;` (Excel с польской и русской локалью делит колонки им), строки — CRLF.
 */

/** Колонки файла — в этом порядке. */
export const SHORT_LINK_CSV_COLUMNS = [
  'id',
  'title',
  'kind',
  'contract',
  'channel',
  'tags',
  'shortUrl',
  'targetUrl',
  'humanClicks',
  'clicks',
  'firstHumanClick',
  'lastClick',
  'expiresAt',
  'state',
  'revokedAt',
  'createdBy',
  'createdAt',
] as const;
export type ShortLinkCsvColumn = typeof SHORT_LINK_CSV_COLUMNS[number];

/** Поля строки, которые попадают в файл (подмножество `ShortLinkFields`). */
export interface IShortLinkCsvRow {
  id: string;
  kind: string;
  title: string;
  channel: string;
  tags: ReadonlyArray<string>;
  code?: string | null;
  url?: string | null;
  targetUrl?: string | null;
  subjectId?: string | null;
  subjectLabel?: string | null;
  clickCount: number;
  humanClickCount: number;
  firstHumanClickedAt?: string | null;
  lastClickedAt?: string | null;
  expiresAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
  createdByName?: string | null;
  state: string;
}

/** Подписи и переводы — со страницы. */
export interface IShortLinkCsvText {
  headers: Record<ShortLinkCsvColumn, string>;
  kind: (kind: string) => string;
  state: (state: string) => string;
  channel: (channel: string) => string;
  /** Название строки ссылки на подпись: «Подпись договора». */
  contractTitle: string;
  /** Вместо адреса ссылки на подпись: «скрыта». */
  /** Автор, если ссылку выдал робот: «Система». */
  system: string;
}

const UTF8_BOM = '﻿';
const CSV_SEP = ';';
const ROW_SEP = '\r\n';

/** Дата и время в файле — местные, сортируемые как текст. */
export const SHORT_LINK_CSV_DATE_FMT = 'YYYY-MM-DD HH:mm';

/** Поле CSV по RFC 4180, но с `;`. */
const csvField = (value: string): string =>
  /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const csvDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(SHORT_LINK_CSV_DATE_FMT) : '';
};

/**
 * CSV списка ссылок: по строке на ссылку, колонки — `SHORT_LINK_CSV_COLUMNS`.
 *
 * 🔴 У ссылки на подпись договора адреса в файле НЕТ, как и на экране:
 * сервер его не отдаёт (bearer-секрет), и колонка честно говорит «скрыта», а
 * не остаётся пустой, как будто ссылки нет вовсе.
 */
export const buildShortLinksCsv = (
  rows: ReadonlyArray<IShortLinkCsvRow>,
  text: IShortLinkCsvText,
): string => {
  const head = SHORT_LINK_CSV_COLUMNS.map((column) => text.headers[column]);
  const lines = rows.map((row) => {
    const isContract = row.kind === 'contract';
    const cells: Record<ShortLinkCsvColumn, string> = {
      id: row.id,
      // Как в таблице: у ссылки на подпись название — «Подпись договора»,
      // у служебной без названия — её вид.
      title: isContract ? text.contractTitle : row.title || text.kind(row.kind),
      kind: text.kind(row.kind),
      contract: isContract
        ? row.subjectLabel || (row.subjectId ? `#${row.subjectId}` : '')
        : '',
      channel: row.channel ? text.channel(row.channel) : '',
      tags: row.tags.join(', '),
      shortUrl: row.url || row.code || '',
      targetUrl: row.targetUrl || '',
      humanClicks: String(row.humanClickCount),
      clicks: String(row.clickCount),
      firstHumanClick: csvDate(row.firstHumanClickedAt),
      lastClick: csvDate(row.lastClickedAt),
      expiresAt: csvDate(row.expiresAt),
      state: text.state(row.state),
      revokedAt: csvDate(row.revokedAt),
      createdBy: row.createdByName || text.system,
      createdAt: csvDate(row.createdAt),
    };
    return SHORT_LINK_CSV_COLUMNS.map((column) => cells[column]);
  });
  return (
    UTF8_BOM +
    [head, ...lines]
      .map((line) => line.map((cell) => csvField(cell)).join(CSV_SEP))
      .join(ROW_SEP)
  );
};

/** Имя файла: `short-links-2026-09-22.csv` (выбранные — `…-selected-…`). */
export const shortLinksCsvFilename = (selected: boolean, now = moment()) =>
  `short-links-${selected ? 'selected-' : ''}${now.format('YYYY-MM-DD')}.csv`;
